<template>
  <div class="department-children-list">
    <!-- Filters -->
    <b-row class="mb-2">
      <b-col
        cols="12"
        md="6"
      >
        <b-input-group>
          <b-form-input
            v-model="filters.search"
            placeholder="Search sub-departments..."
            @keyup.enter="loadChildren"
          />
          <b-input-group-append>
            <b-button
              variant="primary"
              @click="loadChildren"
            >
              <feather-icon icon="SearchIcon" />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col
        cols="12"
        md="3"
      >
        <v-select
          v-model="filters.isActive"
          :options="statusOptions"
          :reduce="option => option.value"
          label="label"
          placeholder="All Statuses"
          clearable
          @input="loadChildren"
        />
      </b-col>
      <b-col
        cols="12"
        md="3"
        class="text-right"
      >
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          @click="addChild"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          Add Sub-Department
        </b-button>
      </b-col>
    </b-row>

    <!-- Table -->
    <b-table
      :items="children"
      :fields="tableColumns"
      :busy="loading"
      responsive
      striped
      hover
      show-empty
      empty-text="No sub-departments found"
    >
      <!-- Loading -->
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-1" />
          <strong>Loading...</strong>
        </div>
      </template>

      <!-- Code -->
      <template #cell(code)="data">
        <b-badge
          variant="light-secondary"
          class="text-monospace"
        >
          {{ data.item.code }}
        </b-badge>
      </template>

      <!-- Name -->
      <template #cell(name)="data">
        <b-link :to="{ name: 'departments-view', params: { id: data.item.id } }">
          {{ data.item.name }}
        </b-link>
      </template>

      <!-- Manager -->
      <template #cell(manager)="data">
        <div v-if="data.item.manager">
          {{ data.item.manager.full_name }}
        </div>
        <b-badge
          v-else
          variant="light-warning"
        >
          No Manager
        </b-badge>
      </template>

      <!-- Employees -->
      <template #cell(employees_count)="data">
        <b-badge
          variant="light-success"
          class="mr-50"
        >
          {{ data.item.active_employees_count }}
        </b-badge>
        <span class="text-muted">/ {{ data.item.employees_count }}</span>
      </template>

      <!-- Status -->
      <template #cell(is_active)="data">
        <b-badge
          :variant="data.item.is_active ? 'light-success' : 'light-secondary'"
        >
          {{ data.item.is_active ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <!-- Actions -->
      <template #cell(actions)="data">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          size="sm"
          @click="viewDepartment(data.item.id)"
        >
          <feather-icon icon="EyeIcon" />
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination
      v-if="pagination.total > pagination.perPage"
      v-model="pagination.currentPage"
      :total-rows="pagination.total"
      :per-page="pagination.perPage"
      align="center"
      @change="handlePageChange"
    />
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BTable,
  BBadge,
  BLink,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { mapActions } from 'vuex'

export default {
  name: 'DepartmentChildrenList',

  components: {
    BRow,
    BCol,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BTable,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
  },

  directives: {
    Ripple,
  },

  props: {
    departmentId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      children: [],
      loading: false,
      filters: {
        search: '',
        isActive: null,
      },
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
      },
      tableColumns: [
        { key: 'code', label: 'Code' },
        { key: 'name', label: 'Name' },
        { key: 'manager', label: 'Manager' },
        { key: 'employees_count', label: 'Employees', class: 'text-center' },
        { key: 'is_active', label: 'Status', class: 'text-center' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],
      statusOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
    }
  },

  created() {
    this.loadChildren()
  },

  methods: {
    ...mapActions('departments', ['fetchDepartmentChildren']),

    async loadChildren() {
      this.loading = true
      try {
        const response = await this.fetchDepartmentChildren({
          id: this.departmentId,
          params: {
            per_page: this.pagination.perPage,
            page: this.pagination.currentPage,
            search: this.filters.search || undefined,
            is_active: this.filters.isActive,
          },
        })
        this.children = response.data || []
        if (response.meta) {
          this.pagination.total = response.meta.total
        }
      } catch (error) {
        console.error('Error loading children:', error)
      } finally {
        this.loading = false
      }
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadChildren()
    },

    viewDepartment(id) {
      this.$router.push({ name: 'departments-view', params: { id } })
    },

    addChild() {
      this.$emit('add-child', { parent_id: this.departmentId })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
